import React, { useState, useEffect } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, addWeeks, subWeeks } from 'date-fns';
import { enUS } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import styles from '../assets/propertyViewingsCalendar.module.css';

const locales = {
  'en-US': enUS
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const PropertyViewingsCalendar = ({ propertyId, viewings, onEventClick }) => {
  const [events, setEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());

  useEffect(() => {
    console.log('📅 [CALENDAR] useEffect triggered with viewings:', viewings);
    
    if (viewings && Array.isArray(viewings) && viewings.length > 0) {
      console.log('📅 [CALENDAR] Processing', viewings.length, 'viewings for calendar');
      
      const calendarEvents = viewings.map((viewing, index) => {
        console.log(`📅 [CALENDAR] Processing viewing ${index + 1}:`, viewing);
        
        // Ensure all required properties exist with fallbacks - SAFE NULL CHECKING
        const title = viewing?.property_title || viewing?.user_title || 
                     (viewing?.first_name && viewing?.last_name ? `${viewing.first_name} ${viewing.last_name}` : 
                     viewing?.notes || viewing?.custom_title || `Event ${index + 1}`);
        
        // Parse date properly - ALWAYS prefer day + time_from/time_to strings
        // because they are pre-formatted in UK timezone by the backend.
        // Using raw Date objects or ISO strings would cause timezone conversion
        // to the browser's local timezone, showing wrong times for non-UK users.
        let startDate, endDate;
        try {
          // Helper to convert day string to yyyy-mm-dd format
          const parseDayString = (dayStr) => {
            if (!dayStr) return null;
            if (dayStr.includes('/')) {
              // dd/mm/yyyy format → yyyy-mm-dd
              const [d, m, y] = dayStr.split('/');
              return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
            }
            if (dayStr.includes('-') && dayStr.length === 10) {
              return dayStr; // Already yyyy-mm-dd
            }
            return dayStr;
          };

          if (viewing?.day && viewing?.time_from) {
            // PREFERRED: Use day + time strings (already in UK timezone from backend)
            const dateString = parseDayString(viewing.day);
            const timeString = viewing.time_from.includes(':') ? viewing.time_from : `${viewing.time_from}:00`;
            // No timezone specifier → treats as local time, so calendar shows UK time as-is
            startDate = new Date(`${dateString}T${timeString}`);
            console.log('📅 [CALENDAR] Created startDate from UK-formatted day+time:', startDate, 'from:', `${dateString}T${timeString}`);
          } else if (viewing?.date) {
            // Fallback: use date object/string (may have timezone issues)
            startDate = new Date(viewing.date);
            console.log('📅 [CALENDAR] Fallback: parsed date from value:', startDate);
          } else {
            console.warn('📅 [CALENDAR] Missing date/day or time_from, using current date');
            startDate = new Date();
          }

          // Validate the created date
          if (isNaN(startDate.getTime())) {
            console.warn('📅 [CALENDAR] Invalid startDate created, using current date');
            startDate = new Date();
          }

          // Handle end date - prefer day + time_to strings (UK timezone)
          if (viewing?.day && viewing?.time_to) {
            const dateString = parseDayString(viewing.day);
            const endTimeString = viewing.time_to.includes(':') ? viewing.time_to : `${viewing.time_to}:00`;
            endDate = new Date(`${dateString}T${endTimeString}`);
            console.log('📅 [CALENDAR] Created endDate from UK-formatted day+time:', endDate, 'from:', `${dateString}T${endTimeString}`);
          } else if (viewing?.date_to) {
            endDate = new Date(viewing.date_to);
            console.log('📅 [CALENDAR] Fallback: parsed date_to from value:', endDate);
          } else {
            // Default to 1 hour after start
            endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
            console.log('📅 [CALENDAR] Using default endDate (1 hour after start):', endDate);
          }

          // Validate the created end date
          if (isNaN(endDate.getTime())) {
            console.warn('📅 [CALENDAR] Invalid endDate created, using start date + 1 hour');
            endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
          }
        } catch (error) {
          console.error('❌ Date parsing error:', error, 'for viewing:', viewing);
          startDate = new Date();
          endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
        }
        
        return {
          id: viewing?.id || Math.random().toString(),
          title: title,
          start: startDate,
          end: endDate,
          resource: viewing,
          className: `viewing-status-${viewing?.status || 0}`
        };
      }).filter(event => {
        // Filter out invalid events
        return event.title && event.start && event.end && 
               !isNaN(event.start.getTime()) && !isNaN(event.end.getTime());
      });
      
      console.log('📅 [CALENDAR] Final calendar events:', calendarEvents);
      console.log('📅 [CALENDAR] Setting', calendarEvents.length, 'events to calendar');
      setEvents(calendarEvents);
    } else {
      console.log('📅 [CALENDAR] No viewings provided or empty array, viewings:', viewings);
      setEvents([]);
    }
  }, [viewings]);

  const handleEventClick = (event) => {
    if (onEventClick && event && event.resource) {
      onEventClick(event.resource);
    }
  };

  const eventStyleGetter = (event) => {
    if (!event || !event.resource) {
      return {
        style: {
          backgroundColor: '#3174ad',
          borderRadius: '4px',
          opacity: 0.95,
          color: 'white',
          border: 'none',
          display: 'flex',
          fontSize: '11px',
          padding: '4px 6px',
          margin: '1px 0',
          minHeight: '70px',
          maxHeight: 'none',
          overflow: 'visible',
          boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
        }
      };
    }

    const viewing = event.resource;
    const status = viewing.status;
    const confirmations = viewing.confirmations_count || 0;

    // Determine background color based on status and confirmations
    // Matching Laravel logic
    let backgroundColor;

    if (confirmations > 0) {
      // Confirmed viewings - Green
      backgroundColor = '#28a745';
    } else if (status === 0) {
      // Upcoming - Blue
      backgroundColor = '#337ab7';
    } else if (status === 1) {
      // Past - Orange
      backgroundColor = '#f0ad4e';
    } else if (status === 2) {
      // Completed - Dark Orange
      backgroundColor = '#cc6600';
    } else if (status === 3) {
      // Uncompleted - Navy
      backgroundColor = '#191970';
    } else {
      // Default - Blue
      backgroundColor = '#3174ad';
    }

    return {
      style: {
        backgroundColor,
        borderRadius: '4px',
        opacity: 0.95,
        color: 'white',
        border: 'none',
        display: 'flex',
        fontSize: '11px',
        padding: '4px 6px',
        margin: '1px 0',
        minHeight: '70px',
        maxHeight: 'none',
        overflow: 'visible',
        boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
        cursor: 'pointer'
      }
    };
  };

  const handleNavigate = (newDate) => {
    setCurrentDate(newDate);
  };

  const handleViewChange = (view) => {
    // Handle view change if needed
  };

  const tooltipAccessor = (event) => {
    if (!event || !event.title) return '';

    const startTime = event.start ? format(event.start, 'HH:mm') : '';
    const endTime = event.end ? format(event.end, 'HH:mm') : '';

    return `${event.title} - ${startTime} to ${endTime}`;
  };

  // Custom event component - simplified for month view, detailed for week/day
  const CustomEvent = ({ event }) => {
    if (!event) return null;

    const startTime = event.start ? format(event.start, 'HH:mm') : '';
    const endTime = event.end ? format(event.end, 'HH:mm') : '';
    const viewing = event.resource;

    // Simple single-line display for month view (matching Laravel compact style)
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        lineHeight: '1.2',
        color: 'white',
        fontSize: '10px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }}>
        <span style={{ fontWeight: '600', marginRight: '4px' }}>
          {startTime}
        </span>
        <span style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {event.title}
        </span>
      </div>
    );
  };

  return (
    <div className={styles.calendarContainer}>
      <Calendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        style={{ height: 700 }}
        defaultView="week"
        views={['month', 'week', 'day']}
        eventPropGetter={eventStyleGetter}
        onSelectEvent={handleEventClick}
        onNavigate={handleNavigate}
        onView={handleViewChange}
        date={currentDate}
        min={new Date(0, 0, 0, 7, 0, 0)} // 7 AM
        max={new Date(0, 0, 0, 23, 0, 0)} // 11 PM
        step={60}
        timeslots={1}
        selectable={false}
        popup={true} // Enable popup for "+X more" link (matching Laravel)
        popupOffset={30} // Offset for popup positioning
        showMultiDayTimes={true} // Show times for multi-day events
        doShowMoreDrillDown={true} // Enable drill-down on "+X more"
        tooltipAccessor={tooltipAccessor}
        components={{
          toolbar: CustomToolbar,
          event: CustomEvent // Custom event component
        }}
      />
    </div>
  );
};

// Custom toolbar component to match the image design
const CustomToolbar = (toolbar) => {
  if (!toolbar) return null;

  const goToBack = () => {
    if (toolbar.onNavigate) {
      toolbar.onNavigate('PREV');
    }
  };

  const goToNext = () => {
    if (toolbar.onNavigate) {
      toolbar.onNavigate('NEXT');
    }
  };

  const goToCurrent = () => {
    if (toolbar.onNavigate) {
      toolbar.onNavigate('TODAY');
    }
  };

  const viewNames = {
    month: 'month',
    week: 'week',
    day: 'day'
  };

  return (
    <div className={styles.customToolbar}>
      <div className={styles.toolbarLeft}>
        <button className={styles.toolbarButton} onClick={goToBack}>
          ‹
        </button>
        <button className={styles.toolbarButton} onClick={goToNext}>
          ›
        </button>
      </div>
      
      <div className={styles.toolbarCenter}>
        <span className={styles.toolbarDate}>
          {toolbar.label || 'Calendar'}
        </span>
      </div>
      
      <div className={styles.toolbarRight}>
        {(toolbar.views || []).map(view => (
          <button
            key={view}
            className={`${styles.toolbarViewButton} ${toolbar.view === view ? styles.active : ''}`}
            onClick={() => toolbar.onView && toolbar.onView(view)}
          >
            {viewNames[view] || view}
          </button>
        ))}
      </div>
    </div>
  );
};

export default PropertyViewingsCalendar;
